import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import SearchBar from "./SearchBar";
import SliderItem from "./SliderItem";
import { recsApi } from "../services/recommendations";
import { smartSearchApi } from "../services/smartSearch";
import { isMobile } from "../utils";
import styled, { css } from "styled-components";
import useDebounce from "../hooks/useDebounce";
import { useSelector, shallowEqual } from "react-redux";

import map from "lodash/map";
import isEqual from "lodash/isEqual";
import get from "lodash/get";

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 650px;
  overflow: hidden;
  z-index: 0;

  ${({ isMobile }) =>
    isMobile &&
    css`
      max-height: 100%;
      height: 100%;
      width: 100%;
      position: absolute;
    `}
`;

const SearchBarWrapper = styled.div`
  display: flex;
  align-content: center;

  ${({ isMobile }) =>
    isMobile &&
    css`
      position: fixed;
      z-index: 1000;
      width: 100%;
      margin-top: 40px;
      flex-direction: column-reverse;
    `}
`;

const GamesWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 15px 5px;
  overflow-y: auto;
  padding-right: 3px;
  margin-top: 20px;
  font-family: ${({ theme }) => theme.fontFamily};
  ${({ theme }) =>
    theme.widgetWidth < 520 &&
    css`
      grid-template-columns: repeat(3, minmax(100px, 1fr));
    `}

  ${({ isMobile }) =>
    isMobile &&
    css`
      max-height: 100%;
      max-width: 100%;
      position: relative;
      margin-top: 120px;
      padding: 0 10px;
    `}

  ::-webkit-scrollbar {
    width: 6px;
  }

  ::-webkit-scrollbar-track {
    background: #151e3d;
  }

  ::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const DismissBtn = styled.button`
  color: white;
  padding: 8px;
  position: relative;
  font-size: 13px;
  font-family: ${({ theme }) => theme.fontFamily};

  font-weight: 700;
  cursor: pointer;
  border: none;
  background-color: transparent;
`;

const SmartSearch = ({ onDismiss, onGameClick }) => {
  const user = useSelector((state) => state.user, shallowEqual);
  const [keywords, setKeywords] = useState("");
  const debouncedSearchVal = useDebounce(keywords, 400);
  const [results, setResults] = useState([]);

  const _isMobile = isMobile();

  const target = useRef(null);

  const { data, error: recsError } = recsApi.useGetRecommendationsQuery(
    { user },
    {
      skip: keywords,
    },
  );

  const recommendedResults = data?.results;

  const { data: searchResults, error: searchError } =
    smartSearchApi.useGetSearchQuery(
      {
        keyword: debouncedSearchVal.trim(),
        userId: user?.userId,
      },
      {
        skip: !keywords || debouncedSearchVal !== keywords,
      },
    );

  const clearSearchResults = () => {
    setKeywords("");
  };

  const error = recsError || searchError;

  useEffect(() => {
    // searchResults return as undefined while typing before returning the actual results.
    // Prevent rerendering in this case and flickering the UI search results.
    //if no input is typed current games should be the recommended
    if (!keywords) {
      setResults(() => recommendedResults);
    } else if (!isEqual(results, searchResults) && searchResults?.length) {
      setResults(() => searchResults);
    }
  }, [results, searchResults, recommendedResults, keywords]);

  return (
    <ContentWrapper isMobile={_isMobile} ref={target}>
      <SearchBarWrapper isMobile={_isMobile}>
        <SearchBar
          onClear={clearSearchResults}
          autoFocus={true}
          placeholder={"Search for games..."}
          value={keywords}
          onChange={(e) => {
            const value = e.target.value;
            value !== "" ? setKeywords(value) : clearSearchResults();
          }}
        />
        {!_isMobile && (
          <DismissBtn data-test="smart-search-dismiss" onClick={onDismiss}>
            Dismiss
          </DismissBtn>
        )}
      </SearchBarWrapper>

      {!error && (
        <GamesWrapper data-test="smart-search-games-list" isMobile={_isMobile}>
          {map(results, (game, idx) => {
            const imageSrc = game.game_image_url || get(game, "images[0].url");
            return (
              <SliderItem
                key={game?.game_id}
                imageSrc={imageSrc}
                className="no-margin-top"
                item={game}
                disableMagnifyOnHover={true}
                onGameClick={onGameClick}
              />
            );
          })}
        </GamesWrapper>
      )}
    </ContentWrapper>
  );
};

SmartSearch.propTypes = {
  onDismiss: PropTypes.func,
};

export default SmartSearch;
